/**
 * Quiz Page Results
 *
 * Result rendering for the standalone quiz page (`templates/page.quiz.json`),
 * where the quiz is rendered inline by `sections/quiz-content.liquid` rather
 * than inside the modal.
 *
 * `quiz.js` only dispatches `quiz:complete` / `quiz:no-results` - it never
 * renders. On the modal route `quiz-popup-stub.js` subscribes; this component
 * is the equivalent subscriber for the page route. Both share the rendering in
 * `@theme/quiz-results`.
 *
 * The modal is present on every page (rendered from `layout/theme.liquid`), so
 * this component listens only for events originating inside its own subtree.
 */

import { QuizResultsRenderer } from '@theme/quiz-results';

class QuizPageResults extends HTMLElement {
  connectedCallback() {
    this.resultsContainer = /** @type {HTMLElement|null} */ (this.querySelector('[data-quiz-results-target]'));
    this.quizContainer = /** @type {HTMLElement|null} */ (this.querySelector('[data-quiz-target]'));

    if (!this.resultsContainer || !this.quizContainer) {
      console.error('Quiz page results: missing target containers');
      return;
    }

    // Snapshot the pristine quiz markup so "Retake Quiz" can restore it.
    this.originalQuizHTML = this.quizContainer.innerHTML;

    this.renderer = new QuizResultsRenderer({
      container: this.resultsContainer,
      config: this.dataset,
      onRestart: () => this.handleRestart(),
    });

    // Bind on this element, not on `document`: the quiz modal renders on every
    // page and its `quiz:complete` must not be handled here.
    this.addEventListener('quiz:complete', this.handleComplete);
    this.addEventListener('quiz:no-results', this.handleNoResults);
  }

  disconnectedCallback() {
    this.removeEventListener('quiz:complete', this.handleComplete);
    this.removeEventListener('quiz:no-results', this.handleNoResults);
  }

  /** @param {Event} event */
  handleComplete = async (event) => {
    const customEvent = /** @type {CustomEvent} */ (event);
    const { result, selections, title } = customEvent.detail;

    const quizComponent = customEvent.target;
    if (quizComponent instanceof Element) {
      QuizResultsRenderer.clearSubmitLoadingState(quizComponent);
    }

    if (!result) return;

    await this.renderer.renderQuizResult(result, title, selections);
    this.showResults();
  };

  handleNoResults = () => {
    const quizComponent = this.querySelector('quiz-component');
    if (quizComponent) {
      QuizResultsRenderer.clearSubmitLoadingState(quizComponent);
    }

    this.renderer.renderNoResults();
    this.showResults();
  };

  /**
   * Swap the quiz out for the results and bring them into view.
   */
  showResults() {
    if (!this.quizContainer || !this.resultsContainer) return;

    this.quizContainer.hidden = true;
    this.resultsContainer.hidden = false;
    this.resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * Restore the pristine quiz and re-run its initialization.
   */
  handleRestart() {
    if (!this.quizContainer || !this.resultsContainer) return;

    this.resultsContainer.hidden = true;
    this.resultsContainer.innerHTML = '';

    this.quizContainer.innerHTML = this.originalQuizHTML || '';
    this.quizContainer.hidden = false;

    // `innerHTML` re-parses the markup, so the new <quiz-component> upgrades
    // and runs connectedCallback on its own. Only already-upgraded elements
    // that were merely re-attached would need a manual nudge.
    const quizComponent = /** @type {any} */ (this.quizContainer.querySelector('quiz-component'));
    if (quizComponent && typeof quizComponent.initializeQuiz === 'function' && quizComponent.questions?.length) {
      quizComponent.initializeQuiz();
    }

    this.quizContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

if (!customElements.get('quiz-page-results')) {
  customElements.define('quiz-page-results', QuizPageResults);
}
